import React, { useState } from "react";

const About = () => {
  const [open, setOpen] = useState({
    connect: false,
    create: false,
    cultivate: false,
  });

  const toggle = (key) =>
    setOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

  return (
    <section
      id="about"
      className="relative min-h-[100vh] w-full bg-[#FFE8BA] overflow-hidden py-14 md:py-20"
    >
      {/* Soft organic background shapes */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 -left-16 h-56 w-56 rounded-full blur-3xl opacity-40 bg-[radial-gradient(circle,_rgba(245,158,11,0.45),transparent_60%)]" />
        <div className="absolute bottom-[-4rem] right-[-3rem] h-72 w-72 rounded-full blur-3xl opacity-40 bg-[radial-gradient(circle,_rgba(234,88,12,0.4),transparent_60%)]" />
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-0 flex flex-col gap-0 md:gap-10">
        {/* --- TITLE BLOCK WITH SEMI-CIRCLE HALO --- */}
        
        {/* <div className="bg-orange-500 w-[20px] h-[30px]"/> */}
        <div className="relative  md:border-l-[40px] border-orange-500/80 pl-5 w-full md:w-[60%] text-center md:text-left mb-1 md:mb-2">
          <div
            className="absolute top-0 left-1/2 md:left-0 -translate-x-1/2 md:translate-x-0 
                        w-[240px] md:w-[300px] lg:w-[360px] aspect-[2/1] rounded-t-full 
                        bg-gradient-to-b from-amber-200 via-orange-300 to-amber-400 
                        opacity-80 -z-10"
          />

          <h1
            className="pt-10 md:pt-0 text-[34px] md:text-[44px] lg:text-[52px] 
                       font-extrabold tracking-tight text-neutral-900 leading-[1.08]"
          >
            About Us
            <span className="block text-neutral-700">
              Our story, roots & vision.
            </span>
          </h1>
        </div>

        <img src="/images/profile_pic.jpg" className="h-[50vh] object-cover object-center translate-y-10"/>

        {/* QUOTE – EMPHASISED, FULL WIDTH UNDER TITLE */}
        <div className="relative mt-0 w-full">
          <span className="pointer-events-none absolute -top-[0px] left-[10px] text-6xl md:text-7xl text-amber-300/70 select-none">
            “
          </span>
          <div className="pl-7 md:pl-10 bg-white/60 p-5 rounded-b-2xl w-full">
            <p className="text-[16px] md:text-[18px] leading-relaxed text-neutral-900 italic">
              “As a child of Zimbabwean immigrants who moved to the UK at 15, I
              often struggled to find places where I could fit in or express my
              love for art and music. There were moments that sparked my
              creativity, but nothing consistent, nothing that felt like home.
              That experience inspired me to build what I wish I’d had — a space
              where creativity thrives all year round.”
            </p>
            <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-amber-900">
              tatenda · director
            </p>
          </div>
        </div>

        {/* MAIN BLOCK: 3 PARAGRAPHS + PHOTO TO THE RIGHT */}
        <div
          className="mt-0 rounded-[32px] bg-white/10 backdrop-blur-sm ring-1 ring-amber-200 shadow-md 
                     px-6 md:px-8 py-7 md:py-9 flex flex-col md:flex-row justify-between gap-8 md:gap-0 items-stretch"
        >
          {/* Text side (3 paragraphs) */}
          <div className="flex-1 flex flex-col justify-between text-justify my-[20px]">
            <p className="text-[16px] md:text-[18px] leading-relaxed text-neutral-900">
              <strong>Bantu Notts CIC</strong> began with a simple belief that
              every young person deserves a space to connect, create and
              cultivate their creative potential.
            </p>

            <p className="text-[16px] md:text-[18px] leading-relaxed text-neutral-900">
              Bantu Notts was born from that experience. Our vision is simple:
              to build spaces where young people can consistently come together,
              connect with others like them or different from them, and create
              art using digital tools and creative expression as a bridge.
            </p>

            <p className="text-[16px] md:text-[18px] leading-relaxed text-neutral-900">
              We want every session to be more than a one-off event. It’s about
              growth that feels real, helping young people find their sound,
              build connections, and shape the future they want to see.
            </p>
          </div>

          {/* Photo side */}
          <div className="flex-1 flex justify-center md:justify-end h-[400px]">
            <div className="relative">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-amber-200/60 blur-2xl -z-10" />
              <div className="absolute bottom-[-10px] right-[-18px] h-20 w-20 rounded-full border-[3px] border-[#AA614A]/70 -z-10" />

              <div className="origin-bottom-right rotate-[-2.5deg] overflow-hidden rounded-[32px] border-[6px] border-white shadow-[0_18px_45px_rgba(0,0,0,0.22)] w-[500px] h-[400px]">
                <img
                  src="/images/aboutus.jpeg"
                  alt="Creative young people making art"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* THREE PILLARS – HERO ACCORDION STYLE */}
        <div className="mt-10 space-y-4">
          <p className="text-xs md:text-sm tracking-[0.28em] uppercase text-neutral-700 mb-1">
            Our three pillars
          </p>

          {/* CONNECT */}
          <div
            className={`group relative rounded-3xl p-[1.5px] transition 
              ${
                open.connect
                  ? "bg-gradient-to-r from-[#AA614A] via-amber-400 to-[#AA614A]"
                  : "bg-gradient-to-r from-amber-200/60 via-white/40 to-amber-200/60"
              }`}
          >
            <div className="rounded-[22px] bg-white/80 backdrop-blur-sm px-4 md:px-5 py-3 md:py-4">
              <button
                type="button"
                onClick={() => toggle("connect")}
                className="w-full flex items-center justify-between text-left"
                aria-expanded={open.connect}
              >
                <div className="flex items-center gap-4">
                  {/* Icon bubble */}
                  <div className="h-9 w-9 rounded-full bg-[#AA614A] text-white flex items-center justify-center text-[11px] font-bold tracking-[0.18em] uppercase shadow-md">
                    C1
                  </div>
                  <div>
                    <p className="text-[13px] md:text-[12px] uppercase tracking-[0.26em] text-[#AA614A]">
                      Connect
                    </p>
                    <p className="text-sm md:text-[15px] text-neutral-900">
                      Bringing young people together.
                    </p>
                  </div>
                </div>

                {/* Arrow */}
                <svg
                  className={`h-5 w-5 text-neutral-800 transition-transform duration-300 
                    ${open.connect ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 8l5 5 5-5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {open.connect && (
                <div className="mt-4 rounded-2xl bg-amber-50/80 border border-amber-200/80 px-4 py-3 text-[14px] md:text-[15px] leading-relaxed text-neutral-900">
                  bringing young people together to build friendships, networks,
                  and community through shared creativity.
                </div>
              )}
            </div>
          </div>

          {/* CREATE */}
          <div
            className={`group relative rounded-3xl p-[1.5px] transition 
              ${
                open.create
                  ? "bg-gradient-to-r from-[#F59E0B] via-orange-400 to-[#F59E0B]"
                  : "bg-gradient-to-r from-amber-200/60 via-white/40 to-amber-200/60"
              }`}
          >
            <div className="rounded-[22px] bg-white/80 backdrop-blur-sm px-4 md:px-5 py-3 md:py-4">
              <button
                type="button"
                onClick={() => toggle("create")}
                className="w-full flex items-center justify-between text-left"
                aria-expanded={open.create}
              >
                <div className="flex items-center gap-4">
                  {/* Icon bubble */}
                  <div className="h-9 w-9 rounded-full bg-[#F59E0B] text-white flex items-center justify-center text-[11px] font-bold tracking-[0.18em] uppercase shadow-md">
                    C2
                  </div>
                  <div>
                    <p className="text-[13px] md:text-[12px] uppercase tracking-[0.26em] text-[#F59E0B]">
                      Create
                    </p>
                    <p className="text-sm md:text-[15px] text-neutral-900">
                      Making, experimenting, playing.
                    </p>
                  </div>
                </div>

                {/* Arrow */}
                <svg
                  className={`h-5 w-5 text-neutral-800 transition-transform duration-300 
                    ${open.create ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 8l5 5 5-5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {open.create && (
                <div className="mt-4 rounded-2xl bg-amber-50/80 border border-amber-200/80 px-4 py-3 text-[14px] md:text-[15px] leading-relaxed text-neutral-900">
                  using music, art, and digital tools to help them explore
                  ideas, make new work, and express who they are.
                </div>
              )}
            </div>
          </div>

          {/* CULTIVATE */}
          <div
            className={`group relative rounded-3xl p-[1.5px] transition 
              ${
                open.cultivate
                  ? "bg-gradient-to-r from-[#92400E] via-amber-500 to-[#92400E]"
                  : "bg-gradient-to-r from-amber-200/60 via-white/40 to-amber-200/60"
              }`}
          >
            <div className="rounded-[22px] bg-white/80 backdrop-blur-sm px-4 md:px-5 py-3 md:py-4">
              <button
                type="button"
                onClick={() => toggle("cultivate")}
                className="w-full flex items-center justify-between text-left"
                aria-expanded={open.cultivate}
              >
                <div className="flex items-center gap-4">
                  {/* Icon bubble */}
                  <div className="h-9 w-9 rounded-full bg-[#92400E] text-white flex items-center justify-center text-[11px] font-bold tracking-[0.18em] uppercase shadow-md">
                    C3
                  </div>
                  <div>
                    <p className="text-[13px] md:text-[12px] uppercase tracking-[0.26em] text-[#92400E]">
                      Cultivate
                    </p>
                    <p className="text-sm md:text-[15px] text-neutral-900">
                      Growing roots and futures.
                    </p>
                  </div>
                </div>

                {/* Arrow */}
                <svg
                  className={`h-5 w-5 text-neutral-800 transition-transform duration-300 
                    ${open.cultivate ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 8l5 5 5-5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {open.cultivate && (
                <div className="mt-4 rounded-2xl bg-amber-50/80 border border-amber-200/80 px-4 py-3 text-[14px] md:text-[15px] leading-relaxed text-neutral-900">
                  developing skills, confidence, and opportunities that open
                  doors to creative careers and lifelong learning.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
